import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const EDGE = 8
const GAP = 4
// What floats now, in the order it opened: opening another closes the rest — two popovers of a
// table's rows or columns never lie over each other — except those it is opened from.
const opened = []

/**
 * Something that floats over the whole page by an anchor — a dropdown, a popover — drawn in
 * `body` with a fixed position (D-152): no scrolling area clips it, no sticky bar or
 * neighbouring tool covers it, and on the document's pages it is not scaled with the paper.
 * It opens below the anchor (or above, when there is more room there), aligned to the
 * anchor's `align` edge, and stays inside the window; it follows its anchor when something
 * scrolls, and closes once the anchor is scrolled out of sight, on a resize, Escape or a
 * press outside.
 *
 * Its layer: over the page, under dialogs (45); opened inside a dialog or a drawer, just over
 * that one — so a confirmation asked from it (a dialog opened later, two layers up) is over it.
 *
 * One at a time: opening one closes any other, except the one it is opened from (a menu in a
 * popover) — the margins' tools stop presses from reaching the page, so a press there alone
 * would not close another.
 *
 * `inside(target)` — the caller's own elements a press may land on without closing it; `fit()` —
 * as wide as its anchor (a field's list of choices).
 */
export function useFloating({ align = () => 'left', inside = () => false, onClose = () => {}, fit = () => false } = {}) {
  const open = ref(false)
  const anchor = ref(null)
  const floating = ref(null)
  const style = ref({ visibility: 'hidden' })

  // The overlay the anchor is in (a dialog, a drawer): the nearest fixed ancestor with a layer.
  function layer() {
    for (let el = anchor.value?.parentElement; el && el !== document.body; el = el.parentElement) {
      const cs = getComputedStyle(el)
      const z = Number.parseInt(cs.zIndex, 10)
      if (cs.position === 'fixed' && Number.isFinite(z)) return z + 1
    }
    return 45
  }
  // Still to be seen: inside the window and every area that clips it.
  function seen() {
    const at = anchor.value?.getBoundingClientRect()
    if (!at || (at.width === 0 && at.height === 0)) return false
    if (at.bottom < 0 || at.top > window.innerHeight || at.right < 0 || at.left > window.innerWidth) return false
    for (let el = anchor.value.parentElement; el && el !== document.body; el = el.parentElement) {
      if (!/(auto|scroll|hidden|clip)/.test(getComputedStyle(el).overflowY + getComputedStyle(el).overflowX)) continue
      const r = el.getBoundingClientRect()
      if (at.bottom < r.top || at.top > r.bottom || at.right < r.left || at.left > r.right) return false
    }
    return true
  }
  let zIndex = 45

  function place() {
    const at = anchor.value?.getBoundingClientRect()
    const el = floating.value
    if (!at || !el) return
    const width = fit() ? at.width : el.offsetWidth
    const height = el.scrollHeight
    const [vw, vh] = [window.innerWidth, window.innerHeight]
    let left = align() === 'right' ? at.right - width : at.left
    if (left + width > vw - EDGE) left = at.right - width
    if (left < EDGE) left = at.left
    left = Math.max(EDGE, Math.min(left, vw - EDGE - width))
    const below = vh - EDGE - (at.bottom + GAP)
    const above = at.top - GAP - EDGE
    const up = height > below && above > below
    const room = up ? above : below
    style.value = {
      zIndex,
      left: `${left}px`,
      top: up ? `${Math.max(EDGE, at.top - GAP - Math.min(height, room))}px` : `${at.bottom + GAP}px`,
      maxHeight: `${Math.max(120, room)}px`,
      ...(fit() ? { width: `${at.width}px` } : {}),
    }
  }
  const self = { close: () => close(), holds: (el) => !!el && (floating.value?.contains(el) || inside(el)) }
  async function show() {
    for (const other of opened.filter((o) => o !== self && !o.holds(anchor.value))) other.close()
    if (!opened.includes(self)) opened.push(self)
    open.value = true
    zIndex = layer()
    style.value = { visibility: 'hidden', zIndex }
    await nextTick()
    place()
  }
  function close() {
    const i = opened.indexOf(self)
    if (i >= 0) opened.splice(i, 1)
    if (!open.value) return
    open.value = false
    onClose()
  }
  const toggle = () => (open.value ? close() : show())
  const within = (target) => anchor.value?.contains(target) || floating.value?.contains(target) || inside(target)
  function pressed(e) {
    if (open.value && e.target?.isConnected && !within(e.target)) close()
  }
  let frame = 0
  function scrolled(e) {
    if (!open.value || floating.value?.contains(e.target) || frame) return
    frame = requestAnimationFrame(() => {
      frame = 0
      if (!open.value) return
      if (seen()) place()
      else close()
    })
  }
  function key(e) {
    if (open.value && e.key === 'Escape') close()
  }
  onMounted(() => {
    document.addEventListener('mousedown', pressed)
    document.addEventListener('scroll', scrolled, true)
    document.addEventListener('keydown', key)
    window.addEventListener('resize', close)
  })
  onBeforeUnmount(() => {
    const i = opened.indexOf(self)
    if (i >= 0) opened.splice(i, 1)
    if (frame) cancelAnimationFrame(frame)
    document.removeEventListener('mousedown', pressed)
    document.removeEventListener('scroll', scrolled, true)
    document.removeEventListener('keydown', key)
    window.removeEventListener('resize', close)
  })
  return { open, anchor, floating, style, show, close, toggle, place }
}
